import React from "react";
import { Formik, Form } from "formik";
import { mapValues, set, get } from "lodash";
import Tabs from "react-bootstrap/Tabs";
import Tab from "react-bootstrap/Tab";
import { object, number, string } from "yup";

import useHttpStatus from "~/commons/hooks/useHttpStatus";
import { initFormValues } from "~/commons/helpers";

import FormGroup from "~/commons/components/FormGroup.jsx";
import FormHttpAlert from "~/commons/components/FormHttpAlert.jsx";
import FormHttpSaveBtn from "~/commons/components/FormHttpSaveBtn.jsx";

import { env } from "~/commons/config";

const bloodline = ["Gold", "Darley", "Areo", "Hayato","Byerley","Godolphin","Blood"];
const byBloodline = (def) =>
  bloodline.reduce((acc, blood) => {
    acc[blood] = def;
    return acc;
  }, {});

const schema = object({
  breeding: object({
    baseFee: object({
      ...byBloodline(number().positive().required()),
    }),
    twins: object({
      bonus: object({
        ...byBloodline(number().positive().max(100).min(0).required()),
      }),
      chance: object({
        ...byBloodline(string().required()),
      }),
    }),
  }),
  racing: object({
    spiritChange: object({
      ...byBloodline(
        string()
          .matches(/^(\s*-?\s*[0-9]+\s*,?\s*){12}$/)
      ),
    }),
  }),
});

const defaults = {
  breeding: {
    baseFee: byBloodline(0),
    twins: {
      bonus: byBloodline(0),
      chance: byBloodline(""),
    },
  },
  racing: {
    spiritChange: "",
  },
};

const preSubmit = (values, actions) => {
  let data = { ...values };
  data = set(
    data,
    "breeding.twins.chance",
    mapValues(get(data, "breeding.twins.chance", {}), (v) =>
      v
        .split(",")
        .map((v) => parseInt(v.trim(), 10))
        .filter((v) => !isNaN(v))
    )
  );

  data = set(
    data,
    "racing.spiritChange",
    mapValues(get(data, "racing.spiritChange", {}), (v) =>
      v
        .split(",")
        .map((v) => parseInt(v.trim(), 10))
        .filter((v) => !isNaN(v))
    )
  );

  return data;
};

const FormConfig = ({ config }) => {
  const [res, req] = useHttpStatus();
  const onSubmit = async (values, actions) => {
    const data = preSubmit(values, actions);
    const { status } = await req({
      method: "patch",
      url: `/api/site/configs/${config._id}`,
      data,
    });

    if (status === "success") {
      actions.setSubmitting(false);
      actions.resetForm({ values: data });
    }
  };

  return (
    <Formik
      initialValues={initFormValues(defaults, {
        ...(config
          ? (() => {
              let data = set(
                config,
                "breeding.twins.chance",
                mapValues(get(config, "breeding.twins.chance", {}), (v) => {
                  if (typeof v === "string") {
                    return v
                      .split(",")
                      .map((v) => parseInt(v.trim(), 10))
                      .filter((v) => !isNaN(v));
                  }
                  return v.map((x) => String(x)).join(", ");
                })
              );

              data = set(
                config,
                "racing.spiritChange",
                mapValues(get(config, "racing.spiritChange", {}), (v) => {
                  if (typeof v === "string") {
                    return v
                      .split(",")
                      .map((v) => parseInt(v.trim(), 10))
                      .filter((v) => !isNaN(v));
                  }
                  return v.map((x) => String(x)).join(", ");
                })
              );
              
              return data;
            })()
          : undefined),
      })}
      validationSchema={schema}
      initialStatus={env?.user?.superAdmin ? undefined : { disabled: true }}
      onSubmit={onSubmit}
    >
      {({ values }) => (
        <Form>
          <FormHttpSaveBtn
            res={res}
            className="btn-primary mb-3"
            disabled={false}
          />
          <FormHttpAlert res={res} />
          <Tabs defaultActiveKey="breeding">
            <Tab eventKey="breeding" title="Breeding">
              <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">
                Base Fee
              </h6>
              <div className="row">
                {bloodline.map((blood) => {
                  return (
                    <div className="col" key={blood}>
                      <FormGroup
                        label={blood}
                        name={`breeding.baseFee.${blood}`}
                        type="number"
                        placeholder="Base fee (GXE)"
                      />
                    </div>
                  );
                })}
              </div>
              <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">
                Twins Bonus (%)
              </h6>
              <div className="row">
                {bloodline.map((blood) => {
                  return (
                    <div className="col" key={blood}>
                      <FormGroup
                        label={blood}
                        name={`breeding.twins.bonus.${blood}`}
                        type="number"
                        placeholder="0 - 100%"
                      />
                    </div>
                  );
                })}
              </div>
              <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">
                Twins Chances (% Comma Separated)
              </h6>
              <div className="row">
                {bloodline.map((blood) => {
                  return (
                    <div className="col" key={blood}>
                      <FormGroup
                        label={blood}
                        name={`breeding.twins.chance.${blood}`}
                      />
                    </div>
                  );
                })}
              </div>
            </Tab>
            <Tab eventKey="racing" title="Racing">
              <br />
              <h6 className="small border-bottom text-muted py-1 mb-3">
                Spirit Change
              </h6>
              <div className="row">
                {bloodline.map((blood) => {
                  return (
                    <div className="col" key={blood}>
                      <FormGroup
                        label={blood}
                        name={`racing.spiritChange.${blood}`}
                        placeholder="4, 2, 1, 0, 0, -2, -2, -4, -6, -8, -10, -12"
                      />
                    </div>
                  );
                })}
              </div>
            </Tab>
          </Tabs>
        </Form>
      )}
    </Formik>
  );
};

export default FormConfig;
